import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { BackgroundMusic } from "@/components/BackgroundMusic";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyCallBar } from "@/components/StickyCallBar";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "robots", content: "index, follow" },
      { name: "author", content: "StillFusion Appliance Repair" },
      { name: "google-site-verification", content: "oZVhax_sha7bWnCVQarhDrVnmDSjlcIJRdVCVman8BI" },
      { property: "og:site_name", content: "StillFusion Appliance Repair" },
      { property: "og:locale", content: "en_US" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/43abb57b-16d1-485a-be3d-91a22c06ff3f",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/43abb57b-16d1-485a-be3d-91a22c06ff3f",
      },
    ],
    links: [{ rel: "stylesheet", href: appCss }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
          "@id": "https://stillfusionappliance.com/#business",
          name: "StillFusion Appliance Repair",
          legalName: "StillFusion LLC",
          alternateName: "StillFusion LLC",
          image: "https://stillfusionappliance.com/favicon.ico",
          logo: "https://stillfusionappliance.com/favicon.ico",
          url: "https://stillfusionappliance.com/",
          telephone: "+1-916-495-3687",
          email: "info@stillfusionappliance.com",
          priceRange: "$$",
          currenciesAccepted: "USD",
          paymentAccepted: "Cash, Credit Card, Debit Card",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Sacramento",
            addressRegion: "CA",
            postalCode: "95814",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 38.5816,
            longitude: -121.4944,
          },
          areaServed: [
            { "@type": "City", name: "Sacramento" },
            { "@type": "City", name: "Roseville" },
            { "@type": "City", name: "Rocklin" },
            { "@type": "City", name: "Folsom" },
            { "@type": "City", name: "Elk Grove" },
            { "@type": "City", name: "Antelope" },
            { "@type": "City", name: "Citrus Heights" },
            { "@type": "City", name: "Carmichael" },
          ],
          serviceType: "Appliance Repair",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Appliance Repair Services",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: "Refrigerator Repair" },
              },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Washer Repair" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Dryer Repair" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Dishwasher Repair" } },
              {
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: "Oven & Stove Repair" },
              },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Microwave Repair" } },
            ],
          },
          description:
            "Professional appliance repair in Sacramento, CA. Fast service for refrigerators, washers, dryers, dishwashers, ovens and microwaves.",
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              opens: "08:00",
              closes: "20:00",
            },
          ],
          sameAs: [],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://stillfusionappliance.com/#website",
          name: "StillFusion Appliance Repair",
          alternateName: "StillFusion Appliance Repair | Sacramento, CA",
          url: "https://stillfusionappliance.com/",
          publisher: { "@id": "https://stillfusionappliance.com/#business" },
          inLanguage: "en-US",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://stillfusionappliance.com/#organization",
          name: "StillFusion Appliance Repair",
          alternateName: "StillFusion Appliance Repair",
          url: "https://stillfusionappliance.com/",
          logo: "https://stillfusionappliance.com/favicon.ico",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="light">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex-1">
          <Outlet />
        </main>
        <SiteFooter />
      </div>
      <StickyCallBar />
      <BackgroundMusic />
    </>
  );
}
